import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, ButtonSize } from '../../../shared/components/button/button';
import { Dropdown } from '../../../shared/components/dropdown/dropdown';
import { Field } from '../../../shared/components/field/field';
import { Icon } from '../../../shared/components/icon/icon';
import { Input } from '../../../shared/components/input/input';
import { ProgressBar } from '../../../shared/components/progress-bar/progress-bar';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { slugify } from '../../../shared/utils/strings';
import type { IDocument } from '../../../verification/queries/verify.graphql';
import type { IVerificationFormData } from '../../containers/document-upload/document-upload-section/document-upload-section';
import { useDocumentUpload } from '../../hooks/use-document-upload';
import type { DocumentSide } from '../../reducers/user-verification.reducer';
import { DragAndDrop } from '../drag-and-drop/drag-and-drop';

import styles from './file-selector.module.scss';

const style = createStyleHelper(styles, 'file-selector');

export interface IFileSelectorProps {
    className?: string;
    hasDocuments: IDocument[];
    onAddMoreDocuments: React.Dispatch<React.SetStateAction<boolean>>;
    documentTypes: string[];
    selectedDocumentType: string;
    kycTypeFieldAmounts: DocumentSide[];
    isDocumentNumberRequired: boolean;
    isDocumentExpirationDateRequired: boolean;
    onDocumentTypeChange: (item: React.ChangeEvent<Element>) => void;
    onDocumentUploadComplete: () => void;
    onDocumentUploadError: () => void;
}

export const FileSelector: FunctionComponent<IFileSelectorProps> = ({
    className,
    hasDocuments,
    onAddMoreDocuments,
    documentTypes,
    selectedDocumentType,
    kycTypeFieldAmounts,
    isDocumentNumberRequired,
    isDocumentExpirationDateRequired,
    onDocumentTypeChange,
    onDocumentUploadComplete,
    onDocumentUploadError,
}) => {
    const intl = useIntl();

    const [showProgressbar, setShowProgressbar] = useState(false);

    const defaultValues: IVerificationFormData = {
        files: {
            FRONT: { documentNumber: '', expiration: '', file: undefined },
            BACK: { documentNumber: '', expiration: '', file: undefined },
        },
    };

    const {
        handleSubmit,
        register,
        control,
        formState: { isValid, errors },
        reset,
    } = useForm<IVerificationFormData>({
        defaultValues,
        mode: 'all',
    });
    const { uploadDocument, sides } = useDocumentUpload();

    const onSubmit = useCallback(
        async (formData: IVerificationFormData) => {
            setShowProgressbar(true);

            try {
                await Promise.all(
                    kycTypeFieldAmounts.map((file) => {
                        const files = formData.files[file];
                        if (!files.file || !file) {
                            return;
                        }
                        return uploadDocument({
                            file: files.file[0],
                            documentType: selectedDocumentType,
                            side: file,
                            documentNumber: formData.files[file].documentNumber ?? '',
                            expiresOn: formData.files[file].expiration
                                ? `${formData.files[file].expiration}T00:00:00Z`
                                : '',
                        });
                    }),
                );

                onDocumentUploadComplete();
            } catch (e) {
                console.error(e);
                onDocumentUploadError();
            }

            // Wait... TODO: Use events!
            await new Promise((resolve) => setTimeout(resolve, 500));

            setShowProgressbar(false);
            onAddMoreDocuments(false);
            reset();
        },
        [
            kycTypeFieldAmounts,
            reset,
            onAddMoreDocuments,
            setShowProgressbar,
            selectedDocumentType,
            uploadDocument,
            onDocumentUploadComplete,
            onDocumentUploadError,
        ],
    );

    return (
        <div className={createClassName([className, style()])}>
            <div
                className={style('text-section', {
                    'has-documents': hasDocuments.length > 0,
                })}
            >
                <div className={style('id-dropdown')}>
                    <Field
                        label={
                            <FormattedMessage
                                id="account.verification.form.document-type.label"
                                defaultMessage="Document Type"
                            />
                        }
                    >
                        <Dropdown
                            className={style('id-type-input')}
                            items={documentTypes.map((item) => ({
                                name: intl.formatMessage({
                                    id: `account.verification.${slugify(item)}`,
                                    defaultMessage: item,
                                }),
                                value: item,
                            }))}
                            onChange={(item) => onDocumentTypeChange(item)}
                        />
                    </Field>
                </div>
                <div className={style('text-description')}>
                    <FormattedMessage
                        id={`account.verification.information.${slugify(selectedDocumentType)}`}
                        defaultMessage={`Please provide your ${selectedDocumentType} information`}
                    />
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                {kycTypeFieldAmounts.map((side) => {
                    return (
                        <Controller
                            key={side}
                            control={control}
                            name={`files.${side}.file`}
                            rules={{
                                required: true,
                                validate: {
                                    lessThan10MB: (file) => (file?.[0]?.size ?? 0) < 10000000,
                                    acceptedFormats: (file) =>
                                        ['image/jpeg', 'image/png', 'application/pdf'].includes(
                                            file?.[0]?.type ?? '',
                                        ),
                                },
                            }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => {
                                return (
                                    <Field
                                        hasError={!!error?.type}
                                        message={
                                            error?.type
                                                ? `file-selector.${slugify(error.type)}.error`
                                                : undefined
                                        }
                                    >
                                        <DragAndDrop onChange={onChange} value={value} side={side}>
                                            <div className={style('with-document')}>
                                                <div className={style('pending-upload')}>
                                                    <div
                                                        className={style('file-section', {
                                                            error: !!error,
                                                        })}
                                                    >
                                                        <div className={style('file-icon')}>
                                                            <Icon
                                                                name={'file-alt'}
                                                                className={style('icon-file')}
                                                            />
                                                        </div>
                                                        <div className={style('file-info')}>
                                                            {value?.[0]?.name ?? ''}
                                                        </div>
                                                        {value?.[0] ? (
                                                            <>
                                                                {isDocumentNumberRequired ? (
                                                                    <div
                                                                        className={style(
                                                                            'file-doc-num',
                                                                        )}
                                                                    >
                                                                        <FormattedMessage
                                                                            id={
                                                                                'account.verification.document-number.label'
                                                                            }
                                                                            defaultMessage={
                                                                                'Document Number'
                                                                            }
                                                                        />
                                                                        <Field
                                                                            hasError={
                                                                                !!errors.files?.[
                                                                                    side
                                                                                ]?.documentNumber
                                                                                    ?.type
                                                                            }
                                                                        >
                                                                            <Input
                                                                                {...register(
                                                                                    `files.${side}.documentNumber`,
                                                                                    {
                                                                                        required:
                                                                                            true,
                                                                                    },
                                                                                )}
                                                                                placeholder={intl.formatMessage(
                                                                                    {
                                                                                        id: 'verification.document-number.placeholder',
                                                                                        defaultMessage:
                                                                                            'ABC 1234 5678',
                                                                                    },
                                                                                )}
                                                                            />
                                                                        </Field>
                                                                    </div>
                                                                ) : null}
                                                                {isDocumentExpirationDateRequired ? (
                                                                    <div
                                                                        className={style(
                                                                            'file-doc-expiry',
                                                                        )}
                                                                    >
                                                                        <FormattedMessage
                                                                            id={
                                                                                'account.verification.expiration.label'
                                                                            }
                                                                            defaultMessage={
                                                                                'Expiration'
                                                                            }
                                                                        />
                                                                        <Field
                                                                            hasError={
                                                                                !!errors.files?.[
                                                                                    side
                                                                                ]?.expiration?.type
                                                                            }
                                                                        >
                                                                            <Input
                                                                                {...register(
                                                                                    `files.${side}.expiration`,
                                                                                    {
                                                                                        required:
                                                                                            true,
                                                                                        validate: {
                                                                                            futureDate:
                                                                                                (
                                                                                                    date,
                                                                                                ) => {
                                                                                                    return (
                                                                                                        Date.now() <
                                                                                                        new Date(
                                                                                                            date,
                                                                                                        ).getTime()
                                                                                                    );
                                                                                                },
                                                                                        },
                                                                                    },
                                                                                )}
                                                                                type="date"
                                                                            />
                                                                        </Field>
                                                                    </div>
                                                                ) : null}
                                                            </>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => onChange(undefined)}
                                                    className={style('delete')}
                                                    type="reset"
                                                >
                                                    <Icon
                                                        name={'trash-alt'}
                                                        className={style('icon-trash')}
                                                    />
                                                </button>
                                            </div>
                                            {showProgressbar ? (
                                                <div className={style('progress-bar')}>
                                                    <ProgressBar
                                                        percentageValue={sides[side].progress}
                                                    />
                                                </div>
                                            ) : null}
                                        </DragAndDrop>
                                    </Field>
                                );
                            }}
                        />
                    );
                })}
                <div className={style('button-section')}>
                    <Button type="submit" disabled={!isValid} size={ButtonSize.MEDIUM}>
                        <FormattedMessage
                            id="kyc-verification.submit"
                            defaultMessage="Upload Documentation"
                        />
                    </Button>
                </div>
            </form>
        </div>
    );
};
